import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle2, FileText, MapPin, Loader2, Info } from 'lucide-react';
import { sendFormEmail } from '../utils/sendFormEmail';
import { getZoneForPosition } from '../data/deliveryZones';
import { getRegionMeta } from '../utils/region';
function getZoneInfo(lat: number, lon: number) {
  const result = getZoneForPosition(lat, lon);
  if (result.zone === 0) {
    return {
      zone: null as number | null,
      fee: 0
    }; // Custom pricing needed
  }
  return {
    zone: result.zone,
    fee: result.fee
  };
}
interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function QuoteModal({
  isOpen,
  onClose
}: QuoteModalProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [addressInput, setAddressInput] = useState('');
  const [addressError, setAddressError] = useState('');
  const [isCalculatingDelivery, setIsCalculatingDelivery] = useState(false);
  const [deliveryZone, setDeliveryZone] = useState<number | null>(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  // Debounced address calculation
  useEffect(() => {
    if (!addressInput.trim()) {
      setDeliveryZone(null);
      setDeliveryFee(0);
      setAddressError('');
      return;
    }
    const timer = setTimeout(async () => {
      setIsCalculatingDelivery(true);
      setAddressError('');
      try {
        const query = encodeURIComponent(addressInput + ', WA');
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
        const data = await res.json();
        if (!data || data.length === 0) {
          setAddressError('Address not found. Please try a more specific address.');
          setDeliveryZone(null);
          setDeliveryFee(0);
        } else {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          const zoneInfo = getZoneInfo(lat, lon);
          setDeliveryZone(zoneInfo.zone);
          setDeliveryFee(zoneInfo.fee);
        }
      } catch (err) {
        setAddressError('Could not calculate delivery. Please try again.');
        setDeliveryZone(null);
        setDeliveryFee(0);
      } finally {
        setIsCalculatingDelivery(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [addressInput]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const inputs = form.querySelectorAll('input');
    const selects = form.querySelectorAll('select');
    const textarea = form.querySelector('textarea');
    const customerName = (inputs[0] as HTMLInputElement)?.value || '';
    const customerEmail = (inputs[2] as HTMLInputElement)?.value || '';
    const rc = getRegionMeta().code;
    const brandName = `${rc} Gravel`;
    // {{message}} is what the EmailJS template renders as the body. Keys with
    // spaces are not valid EmailJS variables, so the detail fields below never
    // reach the email on their own — compose the readable body here.
    const dispatchBody = ['New quote request', '', `Name: ${customerName || '—'}`, `Email: ${customerEmail || '—'}`, `Phone: ${(inputs[1] as HTMLInputElement)?.value || '—'}`, `Delivery address: ${addressInput || '—'}`, `Delivery zone: ${deliveryZone !== null ? `Zone ${deliveryZone} (+${deliveryFee})` : 'Outside standard zones'}`, `Material: ${(inputs[4] as HTMLInputElement)?.value || '—'}`, `Estimated yardage: ${(inputs[5] as HTMLInputElement)?.value || '—'}`, '', 'Project details:', textarea?.value || '—'].join('\n');
    await sendFormEmail({
      subject: `${rc}Gravel — New Quote Request`,
      to_name: customerName,
      from_name: brandName,
      message: dispatchBody,
      Name: customerName,
      'Full Name': customerName,
      Phone: (inputs[1] as HTMLInputElement)?.value || '',
      email: customerEmail,
      _replyto: customerEmail,
      _autoresponse: `Hi ${customerName}! Thank you for requesting a quote from ${brandName}! We've received your details and will get back to you shortly with pricing and availability.\n\nIf you need anything sooner, call us at (206) 619-3742.\n\n- The ${brandName} Team`,
      'Delivery Address': addressInput,
      'Delivery Zone': deliveryZone !== null ? `Zone ${deliveryZone} (+${deliveryFee})` : 'Outside standard zones',
      Material: (inputs[4] as HTMLInputElement)?.value || '',
      'Estimated Yardage': (inputs[5] as HTMLInputElement)?.value || '',
      'Project Details': textarea?.value || ''
    });
    setIsSubmitted(true);
  };
  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setIsSubmitted(false);
      setAddressInput('');
      setDeliveryZone(null);
      setDeliveryFee(0);
    }, 300);
  };
  const inputClass = 'w-full px-3.5 py-3 bg-[#f5f2ee] dark:bg-[#454443] border border-stone-300 dark:border-[#5a5957] rounded-lg text-brand-charcoal dark:text-white placeholder-stone-500 dark:placeholder-stone-400 focus:ring-1 focus:ring-brand-orange/50 focus:border-brand-orange/50 transition-all text-base';
  return <AnimatePresence data-id="element-1512" data-mp="ikvks7wic2rz">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]" data-id="element-1513" data-mp="ilnf7fedbugo" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none" data-id="element-1514" data-mp="1ab8nidz5snbs">
            <div className="w-full max-w-2xl max-h-[90vh] bg-[#e5e1db] dark:bg-[#3a3938] shadow-2xl flex flex-col rounded-2xl border border-stone-400 dark:border-[#4f4e4d] pointer-events-auto overflow-hidden" data-id="element-1515" data-mp="3h2hli78ad28m">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 bg-[#eae6e0] dark:bg-[#3f3e3d] border-b border-stone-400 dark:border-[#4f4e4d]" data-id="element-1516" data-mp="3nhotcyv9qcfy">
                <div className="flex items-center space-x-3" data-id="element-1517" data-mp="39lbezym16r5p">
                  <FileText className="w-5 h-5 text-brand-orange" data-id="element-1518" data-mp="3egi8yepi0hhf" />
                  <h2 className="font-heading text-lg font-bold text-brand-charcoal dark:text-white" data-id="element-1519" data-mp="2zsx7ep89uqbm">
                    Request a Quote
                  </h2>
                </div>
                <button onClick={handleClose} className="p-1.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white transition-colors rounded-full hover:bg-brand-lightgray dark:hover:bg-[#4c4c4c]" data-id="element-1520" data-mp="2qsqzrjz98kkd">
                  <X className="w-5 h-5" data-id="element-1521" data-mp="2gtrp5vuxieuq" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6" data-id="element-1522" data-mp="2lscprwe3f0jp">
                {isSubmitted ? <div className="flex flex-col items-center justify-center text-center space-y-5 py-8" data-id="element-1523" data-mp="2272p9swrlznt">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                damping: 15,
                stiffness: 200
              }} className="w-16 h-16 bg-[#04A05C]/20 dark:bg-[#04A05C]/20 rounded-full flex items-center justify-center" data-id="element-1524" data-mp="1x91514nlruar">
                      <CheckCircle2 className="w-8 h-8 text-[#04A05C] dark:text-[#04A05C]" data-id="element-1525" data-mp="2l43wzlhpedvg" />
                    </motion.div>
                    <div data-id="element-1526" data-mp="2uwt6iatp1ho5">
                      <h3 className="font-heading text-xl font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-1527" data-mp="2uuyj8puy9dgw">
                        Quote Request Sent!
                      </h3>
                      <p className="text-stone-600 dark:text-stone-200 max-w-xs mx-auto" data-id="element-1528" data-mp="33w6iba5aiic1">
                        Thanks for reaching out. We'll get back to you shortly
                        with pricing and availability.
                      </p>
                    </div>
                    <button onClick={handleClose} className="bg-brand-charcoal dark:bg-[#4c4c4c] hover:bg-brand-charcoal/90 dark:hover:bg-[#5d5d5d] text-white px-8 py-2.5 rounded-xl font-bold transition-colors" data-id="element-1529" data-mp="f414p0m0g2es">
                      Done
                    </button>
                  </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1530" data-mp="o2ld98xuwvpa">
                    {/* Row 1: Name & Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1531" data-mp="3sixfc4us9zp1">
                      <div data-id="element-1532" data-mp="3sgtczrcj98qv">
                        <label className="block text-[13px] font-semibold text-stone-700 dark:text-stone-100 mb-2 uppercase tracking-wider" data-id="element-1533" data-mp="fd8qt895qob">
                          Full Name *
                        </label>
                        <input type="text" required className={inputClass} placeholder="John Doe" autoComplete="name" data-id="element-1534" data-mp="34tuyv0bvycw4" />
                      </div>
                      <div data-id="element-1535" data-mp="38t3sm56xearo">
                        <label className="block text-[13px] font-semibold text-stone-700 dark:text-stone-100 mb-2 uppercase tracking-wider" data-id="element-1536" data-mp="31bfanspftew9">
                          Phone Number *
                        </label>
                        <input type="tel" required className={inputClass} placeholder="(253) 555-0123" autoComplete="tel" data-id="element-1537" data-mp="2sah9di6dvlhd" />
                      </div>
                    </div>

                    {/* Row 2: Email & Delivery Address */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1538" data-mp="2idbf612h3ecx">
                      <div data-id="element-1539" data-mp="2dlkyldcxzud2">
                        <label className="block text-[13px] font-semibold text-stone-700 dark:text-stone-100 mb-2 uppercase tracking-wider" data-id="element-1540" data-mp="2dk9z3pqj58rr">
                          Email Address *
                        </label>
                        <input type="email" required className={inputClass} placeholder="john@example.com" autoComplete="email" data-id="element-1541" data-mp="24j1zn4ca21w3" />
                      </div>
                      <div className="md:col-span-2" data-id="element-1542" data-mp="1ump9uhjus86m">
                        <label className="flex items-center justify-between text-[13px] font-semibold text-stone-700 dark:text-stone-100 mb-2 uppercase tracking-wider" data-id="element-1543" data-mp="1upbf2aj9ublo">
                          <span className="flex items-center" data-id="element-1544" data-mp="2rw483g7vyuco">
                            <MapPin className="w-4 h-4 mr-2 text-stone-400" data-id="element-1545" data-mp="35oaeial8lmyx" />
                            Delivery Address *
                          </span>
                          {isCalculatingDelivery && <span className="text-xs text-brand-orange flex items-center normal-case" data-id="element-1546" data-mp="2cz3flahvevd5">
                              <Loader2 className="w-3 h-3 animate-spin mr-1" data-id="element-1547" data-mp="28y9sukg5zmlu" />
                              Calculating...
                            </span>}
                        </label>
                        <input type="text" required value={addressInput} onChange={e => setAddressInput(e.target.value)} className={inputClass} placeholder={`123 Main St, ${getRegionMeta().cities[0]}, WA`} autoComplete="street-address" data-id="element-1548" data-mp="2hyg5y57yakkh" />
                        {addressError && <p className="text-xs text-red-500 mt-1" data-id="element-1549" data-mp="3td3csdpc06wp">
                            {addressError}
                          </p>}
                        {!isCalculatingDelivery && !addressError && addressInput && deliveryZone !== null && <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center" data-id="element-1550" data-mp="1k2khj1c3ppwz">
                              <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-1551" data-mp="1b48uf9ag1a9a" />
                              Zone {deliveryZone} (
                              {deliveryFee === 0 ? 'Free Delivery' : `+$${deliveryFee} Delivery`}
                              )
                            </p>}
                        {!isCalculatingDelivery && !addressError && addressInput && deliveryZone === null && <p className="text-xs text-brand-orange mt-1 flex items-center" data-id="element-1552" data-mp="1p3rpb0e8up3n">
                              <Info className="w-3 h-3 mr-1" data-id="element-1553" data-mp="1u1jtotw2s3oq" />
                              Outside standard zones. We'll provide a custom
                              delivery quote.
                            </p>}
                      </div>
                    </div>

                    {/* Row 3: Material & Yardage */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1554" data-mp="2wdbeqeo30tuw">
                      <div data-id="element-1555" data-mp="2rguggrn64z43">
                        <label className="block text-[13px] font-semibold text-stone-700 dark:text-stone-100 mb-2 uppercase tracking-wider" data-id="element-1556" data-mp="2regpouziu92c">
                          Material Needed
                        </label>
                        <input type="text" className={inputClass} placeholder='e.g. 7/8" Drain Rock, Medium Bark, Pea Gravel' data-id="element-1557" data-mp="2dg9yfxp2v7bn" />
                      </div>
                      <div data-id="element-1558" data-mp="28o8ssumf0eul">
                        <label className="block text-[13px] font-semibold text-stone-700 dark:text-stone-100 mb-2 uppercase tracking-wider" data-id="element-1559" data-mp="28nyxcrilpfhi">
                          Estimated Yardage
                        </label>
                        <input type="text" className={inputClass} placeholder="e.g. 4 truckloads, 50 yards" data-id="element-1560" data-mp="1tsaxfekbfejf" />
                      </div>
                    </div>

                    {/* Row 4: Project Details */}
                    <div data-id="element-1561" data-mp="59ol1kse6fun">
                      <label className="block text-[13px] font-semibold text-stone-700 dark:text-stone-100 mb-2 uppercase tracking-wider" data-id="element-1562" data-mp="585740u7kgw1">
                        Project Details (Optional)
                      </label>
                      <textarea rows={3} className={`${inputClass} resize-none`} placeholder="Tell us about your project or any delivery instructions..." data-id="element-1563" data-mp="eaeggndqq47u"></textarea>
                    </div>

                    <button type="submit" className="w-full bg-[#04A05C] hover:bg-[#048B3A] text-white py-4 rounded-xl font-bold text-base transition-all flex items-center justify-center shadow-lg shadow-[#04A05C]/30 hover:shadow-xl hover:shadow-[#04A05C]/40 uppercase tracking-wide" data-id="element-1564" data-mp="3hs4y2duca0f8">
                      <Send className="w-5 h-5 mr-2" data-id="element-1565" data-mp="alnsminldx8" />
                      Send Quote Request
                    </button>
                  </form>}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}